import { useResetPasswordMutation, useVerifyTokenQuery } from '@api/api';
import { ResetPasswordRequest, VerifyTokenRequest } from '@api/types';
import {
  AuthBlockContainer,
  AuthSuccessMessage,
  Button,
  QueryParameterInput,
  RteBlock,
} from '@components';
import { Alert, Stack, TextField, Typography } from '@mui/material';
import { useForm } from 'react-hook-form';
import { ResponseError, ResponseErrorList } from '../components/ResponseError';

/**
 * Properties type for the Reset Password Block
 */
export type ResetPasswordBlockProps = {
  title?: string;
  text?: string;
  submitLabel?: string;
  successMessage?: string;
  passwordLabel?: string;
  confirmPasswordLabel?: string;
  tokenInvalidText?: string;
};

const getVerifyTokenRequest = (): VerifyTokenRequest => {
  const queryString = window.location.search;
  const urlParams = new URLSearchParams(queryString);
  return {
    email: urlParams.get('email') ?? '',
    token: urlParams.get('token') ?? '',
  };
};

/**
 * Reset Password component
 */
export const ResetPasswordBlock = (props: ResetPasswordBlockProps) => {
  const {
    title,
    text,
    passwordLabel,
    confirmPasswordLabel,
    submitLabel,
    successMessage,
    tokenInvalidText,
  } = props;
  const [trigger, request] = useResetPasswordMutation();
  const { register, handleSubmit } = useForm();
  const errors = (request.error as any)?.data?.errors;

  const verifyTokenResponse = useVerifyTokenQuery(getVerifyTokenRequest());

  const onSubmit = (formData: ResetPasswordRequest) => {
    trigger({ ...formData });
  };

  const form = (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={2}>
          <Typography>{title}</Typography>
          <RteBlock text={text} />
          <TextField
            {...register('Password')}
            name='Password'
            type='password'
            label={passwordLabel}
            fullWidth
          />
          <ResponseError fieldName='Password' errors={errors} sx={{ mb: 2 }} />
          <TextField
            {...register('ConfirmPassword')}
            name='ConfirmPassword'
            label={confirmPasswordLabel}
            fullWidth
            type='password'
          />
          <ResponseError fieldName='ConfirmPassword' errors={errors} />
          <QueryParameterInput {...register('Token')} fieldName='Token' urlParamName='token' />
          <QueryParameterInput {...register('Email')} fieldName='Email' urlParamName='email' />
          <ResponseErrorList errors={errors} excludeKeys={['Password', 'ConfirmPassword']} />
          <Button variant='primary' type='submit' busy={request.isLoading}>
            {submitLabel}
          </Button>
        </Stack>
      </form>
    </>
  );

  const tokenIsValid = (
    <>
      {request.isSuccess && <AuthSuccessMessage message={successMessage} />}
      {!request.isSuccess && form}
    </>
  );

  const tokenIsNotValid = tokenInvalidText ? (
    <Alert severity='error'>
      <Typography
        sx={{
          '& p': {
            margin: '0',
          },
        }}
        dangerouslySetInnerHTML={{ __html: tokenInvalidText }}
      ></Typography>
    </Alert>
  ) : null;

  return (
    <AuthBlockContainer>
      {!verifyTokenResponse.isFetching && (
        <>
          {verifyTokenResponse?.currentData?.data?.success && tokenIsValid}
          {!verifyTokenResponse?.currentData?.data?.success && tokenIsNotValid}
        </>
      )}
    </AuthBlockContainer>
  );
};
